import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState, type MouseEvent } from 'react'
import { ApiError } from '../../api/client'
import {
  createPythonFile,
  pythonFileQuery,
  pythonFileTableQuery,
  pythonFilesShellQuery,
  pythonHierarchyQuery,
  removePythonFile,
  savePythonFile,
  savePythonFilesOrder,
  type PythonFileBrief,
  type PythonFileForm,
  type PythonModule,
  type PythonSaveResult,
} from '../../api/settingsEaseedeskAdamIntopPython'
import { DataTable, useTableState, type TableColumn } from '../../ui/DataTable'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → Adam Intop → Python files, intreg: citire SI scriere.
 *
 * Element cu element fata de `version-3/easeedesk_settings/adam_intop/
 * python_files.php` + `edit_python_file.php` + `python_files_hierarchy.php`,
 * coloana cu coloana fata de `AdamIntopPythonFilesModel` din
 * `public/js/settings/easeedesk/adam_intop_settings.js`.
 *
 * FILA DIN RANDUL DESFASURAT E CHIAR FORMULARUL
 * ---------------------------------------------
 *
 * In portal, `+` pe un rand aducea prin ajax `python_file_tabs` - o macheta de
 * 15 randuri cu O SINGURA fila, care ducea la `edit_python_file`. Un ocol
 * printr-un cap de API care intoarce HTML, pentru o fila. Aici randul
 * desfasurat deschide direct formularul.
 *
 * EDITORUL E UN `<textarea>`, NU ACE
 * ----------------------------------
 *
 * Macheta veche punea peste textarea editorul Ace (`ace/mode/python`), incarcat
 * din antetul portalului. In /app biblioteca aia nu exista inca; textul se
 * scrie intr-un `<textarea>` cu font fix, si trece pe acelasi camp `editor` din
 * cerere. Cand cineva aduce Ace in /app, se pune peste - contractul cererii nu
 * se schimba.
 */

const TABLE_ID = 'adam-intop-python-files'

export function esc(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

/** `AdamIntopPythonFilesModel`: aceleasi unsprezece coloane, in aceeasi ordine. */
export function pythonFileColumns(): TableColumn[] {
  const text = (key: string): TableColumn['html'] => (row) => esc(String(row[key] ?? ''))
  return [
    {
      index: 0,
      title: '',
      orderable: false,
      visible: true,
      width: 30,
      html: (row) =>
        `<center class="show_details color-dark-grey pointer font-16" data-id="${Number(row.ID)}" data-type=""><i class="fas fa-plus-circle"></i></center>`,
    },
    { index: 1, title: 'ID', orderable: true, visible: true, width: 30, className: 'a-left', html: text('ID') },
    { index: 2, title: 'File name', orderable: true, visible: true, className: 'a-left', html: text('filename') },
    { index: 3, title: 'File path', orderable: true, visible: true, className: 'a-left', html: text('filepath') },
    {
      index: 4,
      title: 'Assigned Adam Intop Module',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: text('module_name'),
    },
    { index: 5, title: 'Parent of', orderable: true, visible: true, className: 'a-left', html: text('parent_of_file') },
    { index: 6, title: 'Child of', orderable: true, visible: true, className: 'a-left', html: text('child_of_file') },
    { index: 7, title: 'File order', orderable: true, visible: true, className: 'a-left', html: text('file_order') },
    { index: 8, title: 'Type', orderable: true, visible: true, className: 'a-left', html: text('type') },
    {
      index: 9,
      title: 'Status',
      orderable: false,
      visible: true,
      className: 'a-left',
      html: (row) =>
        String(row.status) === '1'
          ? '<span class="label label-success">Active</span>'
          : '<span class="label label-danger">Inactive</span>',
    },
    {
      index: 10,
      title: 'Actions',
      orderable: false,
      visible: true,
      width: 130,
      html: (row) =>
        `<a href="javascript:void(0)" data-id="${Number(row.ID)}" class="btn btn-xs btn-danger btn-delete-python-file pull-left" style="margin-left:6px"> <i class="fas fa-times" aria-hidden="true"></i> Delete </a>`,
    },
  ]
}

/** Coloana SQL a fiecarui indice, ca in `getAdamIntopPythonFiles()`. */
export const ORDER_NAMES = ['ID', 'ID', 'filename', 'filepath', 'module_id', 'parent_of_file', 'child_of_file', 'file_order', 'type', 'status', 'ID']

export const EMPTY_FORM: PythonFileForm = {
  filename: '',
  filepath: '',
  parent_of_file: '',
  child_of_file: '',
  file_order: '',
  type: '',
  module_id: '',
}

export function AdamIntopPythonFilesPage() {
  const queryClient = useQueryClient()
  const shell = useQuery(pythonFilesShellQuery())
  /* `order: [[1, 'desc']]` - pe ce si cum porneste lista veche. */
  const [state, setState] = useTableState(TABLE_ID, {
    start: 0,
    length: 25,
    order: { column: 1, dir: 'desc' },
    search: '',
  })
  const [creating, setCreating] = useState(false)
  const columns = useMemo(() => pythonFileColumns(), [])
  const table = useQuery({
    ...pythonFileTableQuery({
      draw: 1,
      start: state.start,
      length: state.length,
      orderColumn: state.order.column,
      orderName: ORDER_NAMES[state.order.column] ?? 'ID',
      orderDir: state.order.dir,
      search: state.search,
    }),
    placeholderData: keepPreviousData,
  })

  const redraw = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'adam_intop', 'python_files'] })
  }

  const remove = useMutation({
    mutationFn: (file: number) => removePythonFile(file),
    onSuccess: () => {
      toast('File removed.', 'Success')
      redraw()
    },
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  if (shell.error instanceof ApiError && shell.error.status === 403) return <Forbidden />
  if (!shell.data) return <Spinner />

  const rows = (table.data?.data ?? []) as unknown as Record<string, unknown>[]

  const onClick = (event: MouseEvent<HTMLDivElement>) => {
    const kill = (event.target as HTMLElement).closest('.btn-delete-python-file')
    if (kill instanceof HTMLElement) {
      event.preventDefault()
      /* Intrebarea din swal, cu acelasi text. */
      if (!window.confirm('Are you sure you want to delete this file?\nMissing data may cause serious problems.')) return
      remove.mutate(Number(kill.dataset.id ?? 0))
    }
  }

  return (
    <div onClick={onClick}>
      <button type="button" className="pull-right btn btn-success btn-sm" onClick={() => setCreating(true)}>
        <i className="fas fa-plus" aria-hidden="true"></i> Add New Python File
      </button>
      <h4>
        <strong>Python</strong> Files
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage, create or edit <strong>python files</strong>
      </label>
      <hr />
      <DataTable
        id={TABLE_ID}
        columns={columns}
        rows={rows}
        total={table.data?.recordsFiltered ?? 0}
        fetching={table.isFetching}
        error={table.error ? table.error.message : null}
        onRetry={() => void table.refetch()}
        state={state}
        onState={setState}
        rowId={(row) => String(row.DT_RowId ?? `row_${Number(row.ID)}`)}
        emptyHint="A python file is a step Adam Intop can run on a module."
        details={(row) => <PythonFilePanel file={Number(row.ID)} onSaved={redraw} />}
      />
      <NewPythonFileModal
        open={creating}
        modules={shell.data.modules}
        files={shell.data.files}
        onClose={() => setCreating(false)}
        onSaved={redraw}
      />
    </div>
  )
}

function Forbidden() {
  return (
    <div className="alert alert-danger">
      <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
    </div>
  )
}

/** Ce spune raspunsul cand randul s-a scris dar textul n-a ajuns pe server. */
function announce(result: PythonSaveResult, done: string): void {
  if (result.stored === false) {
    toast(result.message ?? 'Saved, but the file content could not be written.', 'Warning', 'error')
    return
  }
  toast(done, 'Success')
}

/**
 * Randul desfasurat: fisa fisierului si textul lui, adica `edit_python_file`.
 *
 * Cand `content` vine null, serverul de fisiere n-a raspuns: editorul se
 * stinge, ca sa nu plece la salvare un text gol peste unul bun.
 */
export function PythonFilePanel({ file, onSaved }: { file: number; onSaved(): void }) {
  const query = useQuery(pythonFileQuery(file))
  const [form, setForm] = useState<PythonFileForm>(EMPTY_FORM)
  const [editor, setEditor] = useState('')

  useEffect(() => {
    const data = query.data
    if (!data) return
    setForm({
      filename: data.file.filename,
      filepath: data.file.filepath,
      parent_of_file: data.file.parent_of_file,
      child_of_file: data.file.child_of_file,
      file_order: data.file.file_order,
      type: data.file.type,
      module_id: String(data.file.module_id || ''),
    })
    setEditor(data.content ?? '')
  }, [query.data])

  const save = useMutation({
    mutationFn: () => savePythonFile(file, form, editor),
    onSuccess: (result) => {
      announce(result, 'File saved.')
      onSaved()
    },
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  if (query.error instanceof ApiError && query.error.status === 403) return <Forbidden />
  if (!query.data) return <Spinner />

  return (
    <div style={{ padding: '0px 15px' }}>
      <h4>
        <strong>Edit</strong> Python File
      </h4>
      <form
        method="post"
        onSubmit={(e) => {
          e.preventDefault()
          save.mutate()
        }}
      >
        <PythonFileFields
          idPrefix={`edit-${file}`}
          modules={query.data.modules}
          files={query.data.files}
          form={form}
          onChange={setForm}
        />
        <PythonEditor
          id={`edit-editor-${file}`}
          value={editor}
          offline={query.data.content === null}
          onChange={setEditor}
        />
        <div className="form-group mt-2 text-center center-block">
          <button type="submit" className="btn btn-primary" disabled={save.isPending}>
            Save
          </button>
        </div>
      </form>
    </div>
  )
}

/** `newResource` din `python_files.php`, cu formularul lui `create_python_file_save`. */
export function NewPythonFileModal({
  open,
  modules,
  files,
  onClose,
  onSaved,
}: {
  open: boolean
  modules: PythonModule[]
  files: PythonFileBrief[]
  onClose(): void
  onSaved(): void
}) {
  const [form, setForm] = useState<PythonFileForm>(EMPTY_FORM)
  const [editor, setEditor] = useState('')

  /* `hidden.bs.modal` golea formularul; aici il golim la deschidere. */
  useEffect(() => {
    if (open) {
      setForm(EMPTY_FORM)
      setEditor('')
    }
  }, [open])

  const create = useMutation({
    mutationFn: () => createPythonFile(form, editor),
    onSuccess: (result) => {
      announce(result, 'File created.')
      onClose()
      onSaved()
    },
    onError: (error) =>
      toast(error instanceof ApiError ? error.message : 'Something went wrong.', 'Error', 'error'),
  })

  return (
    <Modal id="newPythonFile" labelledBy="newPythonFileLabel" open={open} onClose={onClose} title="Add New Python File">
      <form
        method="post"
        onSubmit={(e) => {
          e.preventDefault()
          if (form.filename.trim() === '' || form.module_id === '') return
          create.mutate()
        }}
      >
        <PythonFileFields idPrefix="new" modules={modules} files={files} form={form} onChange={setForm} />
        <PythonEditor id="new-editor" value={editor} offline={false} onChange={setEditor} />
        <div className="form-group aright">
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>{' '}
          <button
            type="submit"
            className="btn btn-primary"
            disabled={form.filename.trim() === '' || form.module_id === '' || create.isPending}
          >
            Save
          </button>
        </div>
      </form>
    </Modal>
  )
}

/** Cele sase campuri ale fisei, aceleasi in modalul de creare si in fila de editare. */
function PythonFileFields({
  idPrefix,
  modules,
  files,
  form,
  onChange,
}: {
  idPrefix: string
  modules: PythonModule[]
  files: PythonFileBrief[]
  form: PythonFileForm
  onChange(next: PythonFileForm): void
}) {
  const set = (key: keyof PythonFileForm) => (value: string) => onChange({ ...form, [key]: value })
  return (
    <div className="row">
      <div className="col-md-6">
        <div className="form-group">
          <label htmlFor={`${idPrefix}-filename`} className="control-label">
            Filename <span className="required">*</span>
          </label>
          <input
            id={`${idPrefix}-filename`}
            type="text"
            className="form-control input-sm"
            name="filename"
            value={form.filename}
            onChange={(e) => set('filename')(e.target.value)}
          />
        </div>
      </div>
      <div className="col-md-6">
        <div className="form-group">
          <label htmlFor={`${idPrefix}-module`} className="control-label">
            Assigned Module <span className="required">*</span>
          </label>
          <select
            id={`${idPrefix}-module`}
            className="form-control input-sm"
            name="module_id"
            value={form.module_id}
            onChange={(e) => set('module_id')(e.target.value)}
          >
            <option value=""> - assigned module - </option>
            {modules.map((m) => (
              <option key={m.ID} value={m.ID}>
                {m.module_name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="clearfix"></div>
      <div className="col-md-4">
        <div className="form-group">
          <label htmlFor={`${idPrefix}-parent`} className="control-label">
            Parent for
          </label>
          <select
            id={`${idPrefix}-parent`}
            className="form-control input-sm"
            name="parent_of_file"
            value={form.parent_of_file}
            onChange={(e) => set('parent_of_file')(e.target.value)}
          >
            <option value=""> - parent for - </option>
            {files.map((f) => (
              <option key={f.ID} value={f.ID}>
                {f.filename}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="col-md-4">
        <div className="form-group">
          <label htmlFor={`${idPrefix}-child`} className="control-label">
            Child for
          </label>
          <select
            id={`${idPrefix}-child`}
            className="form-control input-sm"
            name="child_of_file"
            value={form.child_of_file}
            onChange={(e) => set('child_of_file')(e.target.value)}
          >
            <option value=""> - child for - </option>
            {files.map((f) => (
              <option key={f.ID} value={f.ID}>
                {f.filename}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="col-md-4">
        <div className="form-group">
          <label htmlFor={`${idPrefix}-order`} className="control-label">
            File Order
          </label>
          <select
            id={`${idPrefix}-order`}
            className="form-control input-sm"
            name="file_order"
            value={form.file_order}
            onChange={(e) => set('file_order')(e.target.value)}
          >
            <option value=""> - order - </option>
            {Array.from({ length: 101 }, (_, i) => (
              <option key={i} value={i}>
                {i}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  )
}

/** Textarea cu font fix in locul lui Ace - vezi capul fisierului. */
function PythonEditor({
  id,
  value,
  offline,
  onChange,
}: {
  id: string
  value: string
  offline: boolean
  onChange(next: string): void
}) {
  return (
    <div className="row">
      <div className="col-md-12">
        <div className="form-group">
          <label htmlFor={id} className="control-label">
            File content editor <span className="required">*</span>
          </label>
          {offline ? (
            <div className="alert alert-warning">
              <i className="fas fa-exclamation-triangle" aria-hidden="true"></i> The python server did not answer, so the
              file content could not be read. Editing is off, to keep an empty editor from overwriting a good file.
            </div>
          ) : (
            <textarea
              id={id}
              name="editor"
              className="form-control"
              spellCheck={false}
              style={{ minHeight: 400, fontFamily: 'monospace', fontSize: 12 }}
              value={value}
              onChange={(e) => onChange(e.target.value)}
            />
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * `python_files_hierarchy.php`: fisierele unui modul, parintii cu copiii lor.
 *
 * SE VEDE, NU SE TRAGE CU MANA. In portal lista era `jquery-sortable` si Save
 * trimitea sirul facut de `sortable("serialize")` - un arbore
 * `{id, children: [...]}` - pe care `send_python_file_order()` il scria, ca
 * atare, in COLOANA `children`. Un tablou intr-o coloana scalara: interogarea
 * nu se lega, si ordinea nu se salva niciodata. Legatura care chiar tine
 * arborele e `child_of_file`, si ea se alege din fisa fiecarui fisier.
 *
 * Deci aici arborele se ARATA - din `child_of_file`, cum il deseneaza si
 * macheta veche - si butonul Save trimite acelasi arbore pe acelasi cap, ca sa
 * ramana o singura forma de cerere pana cand cineva hotaraste ce trebuia sa
 * scrie coloana `children`. Cine vrea sa mute un fisier sub altul o face din
 * "Child for", in fila de editare.
 *
 * Se foloseste din fila "Files Hierarchy" a MODULELOR extinse, exact unde o
 * punea `extended_module_tabs`.
 */
export function PythonFilesHierarchyPanel({ module }: { module: number }) {
  const query = useQuery(pythonHierarchyQuery(module))

  const save = useMutation({
    mutationFn: () => {
      const files = query.data?.files ?? []
      return savePythonFilesOrder(
        module,
        files.map((file) => ({
          id: file.ID,
          children: files.filter((child) => String(child.child_of_file) === String(file.ID)).map((child) => child.ID),
        })),
      )
    },
    onSuccess: () => toast('Files hierarchy saved.', 'Success'),
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  if (query.error instanceof ApiError && query.error.status === 403) return <Forbidden />
  if (!query.data) return <Spinner />

  return (
    <div style={{ padding: '0px 15px' }}>
      <h4>
        <strong>Python</strong> Files Hierarchy
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage, create or edit <strong>python files hierarchy</strong>
      </label>
      <hr />
      {query.data.files.length === 0 ? (
        <div className="alert alert-info">
          <i className="fas fa-info-circle" aria-hidden="true"></i> No python files on this module.
        </div>
      ) : (
        <ol className="python-hierarchy serialization vertical">
          {query.data.files.map((file) => (
            <li key={file.ID} data-id={file.ID} data-name={file.filename}>
              <i className="fab fa-python" aria-hidden="true"></i> {file.filename}
              <ol>
                {query.data.files
                  .filter((child) => String(child.child_of_file) === String(file.ID))
                  .map((child) => (
                    <li key={child.ID} data-id={child.ID}>
                      <i className="fab fa-python" aria-hidden="true"></i> {child.filename}
                    </li>
                  ))}
              </ol>
            </li>
          ))}
        </ol>
      )}
      <div className="form-group mt-2 text-center center-block">
        <button type="button" className="btn btn-primary" disabled={save.isPending} onClick={() => save.mutate()}>
          Save
        </button>
      </div>
    </div>
  )
}
